import React, { useMemo } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useSaveEligibilityReport } from '@frontend/insurance-verification-reports-writeback';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Heading, Button } from '@frontend/design-system';
import { insuranceReportTrackingIds } from '../constants';
import {
  useDentalInsuranceReportContext,
  useInsuranceEligibilityReportContext,
} from '../dental-insurance-eligibility-report';
import { useInsuranceEligibilityReportContainerContext } from '../providers/insurance-report-container.provider';
import { Filters } from './filters';
import { InsuranceReportWritebackStatusMessageContainer } from './insurance-report-writeback-status-message';

type HeaderProps = {
  handlePrint?: () => void;
  handleSaveToPms?: () => void;
  style?: SerializedStyles;
  showSaveToPmsButton?: boolean;
  showPrintButton?: boolean;
  showFilters?: boolean;
};

export const Header = React.memo(
  ({
    handlePrint,
    handleSaveToPms,
    showSaveToPmsButton = true,
    showPrintButton = true,
    showFilters = true,
    style,
  }: HeaderProps) => {
    const { t } = useTranslation('insuranceEligibilityReport');
    const { isEligibilityReportWrittenBackInPMS } = useSaveEligibilityReport();
    const { profileData } = useInsuranceEligibilityReportContext();
    const { integrationDetails: { isDocumentCenterWritebackSupported, PMSName } = {} } =
      useInsuranceEligibilityReportContainerContext();

    const {
      insuranceEligibilityReportData: { dentalSummaryInfo: { patient } = {} },
    } = useDentalInsuranceReportContext();

    const shouldShowSaveToPmsButton =
      isDocumentCenterWritebackSupported && PMSName && !isEligibilityReportWrittenBackInPMS && showSaveToPmsButton;

    const reportPersonName = useMemo<string>(() => {
      const firstName = profileData?.firstName || patient?.firstName;
      if (firstName) {
        return `${titleCase(firstName)}'s`;
      }
      return '';
    }, [profileData, patient]);

    if (!reportPersonName) return null;

    const translationReportPersonName = t('{{reportPersonName}} Insurance Verification Report', {
      reportPersonName,
    });

    return (
      <header css={[headerStyle, style]}>
        <Heading level={1}>{translationReportPersonName.trim()}</Heading>
        <div css={buttonContainerStyle}>
          <InsuranceReportWritebackStatusMessageContainer />
          {showFilters && <Filters />}
          {showPrintButton && (
            <Button
              trackingId={insuranceReportTrackingIds.printReportDataTrackingId}
              css={buttonStyle}
              onClick={handlePrint}
            >
              {t('Print Report')}
            </Button>
          )}
          {shouldShowSaveToPmsButton && (
            <Button
              variant='secondary'
              onClick={handleSaveToPms}
              trackingId={insuranceReportTrackingIds.saveToPMSTrackingId}
              css={{ width: 'fit-content' }}
            >
              {t('Save to {{PMSName}}', { PMSName })}
            </Button>
          )}
        </div>
      </header>
    );
  }
);

Header.displayName = 'Header';

const headerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin: ${theme.spacing(0, 0, 4, 0)};
`;

const buttonStyle = css`
  width: auto;
  white-space: nowrap;
  font-size: ${theme.fontSize(16)};
  padding: ${theme.spacing(1, 2)};
`;

const buttonContainerStyle = css`
  background-color: transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing(2)};

  @media print {
    display: none;
  }
`;
